function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function isTiptapDoc(value) {
    return isRecord(value) && value.type === 'doc' && Array.isArray(value.content);
}
function createTextNode(text, marks) {
    return marks && marks.length > 0 ? { type: 'text', text, marks } : { type: 'text', text };
}
function createParagraphFromText(text) {
    // Split on real newlines AND inline <br> markers (GFM convention for line breaks
    // inside a single line - e.g. multi-line table cells); each break becomes a hardBreak.
    const lines = text.split(/\r?\n|<br\s*\/?>/i);
    const content = [];
    for (let index = 0; index < lines.length; index += 1) {
        const line = lines[index] ?? '';
        if (line) {
            content.push(...parseInlineMarkdown(line));
        }
        if (index < lines.length - 1) {
            content.push({ type: 'hardBreak' });
        }
    }
    return content.length > 0 ? { type: 'paragraph', content } : { type: 'paragraph' };
}
function splitMarkdownTableRow(line) {
    const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
    // Split on unescaped pipes only; honor a `\|` escape as a literal pipe inside a
    // cell (the inverse of serializeTable, which escapes literal pipes in cell text).
    const cells = [];
    let buf = '';
    for (let i = 0; i < trimmed.length; i += 1) {
        const ch = trimmed[i];
        if (ch === '\\' && trimmed[i + 1] === '|') {
            buf += '|';
            i += 1;
        }
        else if (ch === '|') {
            cells.push(buf.trim());
            buf = '';
        }
        else {
            buf += ch;
        }
    }
    cells.push(buf.trim());
    return cells;
}
function isMarkdownTableSeparator(line) {
    const cells = splitMarkdownTableRow(line);
    return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}
function isMarkdownTableStart(lines, index) {
    if (index + 1 >= lines.length)
        return false;
    const header = lines[index]?.trim() ?? '';
    const separator = lines[index + 1]?.trim() ?? '';
    return header.includes('|') && isMarkdownTableSeparator(separator);
}
function getCodeFenceMarker(line) {
    const trimmed = line.trimStart();
    if (trimmed.startsWith('```'))
        return '```';
    if (trimmed.startsWith('~~~'))
        return '~~~';
    return null;
}
function findNextInlineToken(text, start) {
    // `{{` opens an atomic `{{protoss:...}}` binding token - matched first/whole so inline
    // markdown never looks inside it (a snake_case path like `calc.sow_expiration` would
    // otherwise be split at its `_`, fragmenting the token across runs so it never resolves).
    const tokens = ['{{', '[', '**', '__', '~~', '==', '`', '*', '_'];
    let best = null;
    for (const token of tokens) {
        const index = text.indexOf(token, start);
        if (index === -1)
            continue;
        if (!best || index < best.index || (index === best.index && token.length > best.token.length)) {
            best = { index, token };
        }
    }
    return best;
}
function parseDelimitedMark(text, token, markType) {
    if (!text.startsWith(token))
        return null;
    const endIndex = text.indexOf(token, token.length);
    if (endIndex <= token.length)
        return null;
    const inner = text.slice(token.length, endIndex);
    if (!inner)
        return null;
    const marks = [{ type: markType }];
    const nodes = markType === 'code'
        ? [createTextNode(inner, marks)]
        : applyMarksToNodes(parseInlineMarkdown(inner), marks);
    return { nodes, length: endIndex + token.length };
}
function applyMarksToNodes(nodes, marks) {
    return nodes.map((node) => {
        if (node.type !== 'text')
            return node;
        const existingMarks = Array.isArray(node.marks)
            ? node.marks
            : [];
        return {
            ...node,
            marks: [...existingMarks, ...marks],
        };
    });
}
function parseLink(text) {
    if (!text.startsWith('['))
        return null;
    const closingBracket = text.indexOf(']');
    if (closingBracket <= 0 || text[closingBracket + 1] !== '(')
        return null;
    const closingParen = text.indexOf(')', closingBracket + 2);
    if (closingParen === -1)
        return null;
    const label = text.slice(1, closingBracket);
    const href = text.slice(closingBracket + 2, closingParen).trim();
    if (!label || !href)
        return null;
    const marks = [{ type: 'link', attrs: { href } }];
    return {
        nodes: applyMarksToNodes(parseInlineMarkdown(label), marks),
        length: closingParen + 1,
    };
}
/**
 * Consume an atomic `{{...}}` binding token as a single text node, so inline markdown
 * (emphasis at an intraword `_`/`*` inside the token path) never fragments it. Returns
 * null for a bare `{{` with no closing `}}` (falls through to literal-char handling).
 */
function parseToken(text) {
    if (!text.startsWith('{{'))
        return null;
    const end = text.indexOf('}}', 2);
    if (end === -1)
        return null;
    const full = text.slice(0, end + 2);
    return { nodes: [createTextNode(full)], length: full.length };
}
function parseInlineMarkdown(text) {
    const nodes = [];
    let cursor = 0;
    while (cursor < text.length) {
        const nextToken = findNextInlineToken(text, cursor);
        if (!nextToken) {
            const remainder = text.slice(cursor);
            if (remainder)
                nodes.push(createTextNode(remainder));
            break;
        }
        if (nextToken.index > cursor) {
            nodes.push(createTextNode(text.slice(cursor, nextToken.index)));
            cursor = nextToken.index;
        }
        const fragment = text.slice(cursor);
        const parsed = parseToken(fragment) ??
            parseLink(fragment) ??
            parseDelimitedMark(fragment, '**', 'bold') ??
            parseDelimitedMark(fragment, '__', 'bold') ??
            parseDelimitedMark(fragment, '~~', 'strike') ??
            parseDelimitedMark(fragment, '==', 'highlight') ??
            parseDelimitedMark(fragment, '`', 'code') ??
            parseDelimitedMark(fragment, '*', 'italic') ??
            parseDelimitedMark(fragment, '_', 'italic');
        if (!parsed) {
            nodes.push(createTextNode(text[cursor] ?? ''));
            cursor += 1;
            continue;
        }
        nodes.push(...parsed.nodes);
        cursor += parsed.length;
    }
    return nodes;
}
/** Matches a list item at any indentation: leading whitespace, a bullet/ordinal marker, then text. */
const LIST_ITEM_RE = /^(\s*)([-*+]|\d+\.)\s+(.*)$/;
function makeListItem(text) {
    return { type: 'listItem', content: [createParagraphFromText(text)] };
}
/**
 * Build a nested Tiptap list tree from a flat, source-ordered list of items, nesting each item
 * under the most recent shallower one by its indentation. A deeper indent starts a child list;
 * an equal indent with the same marker kind continues the current list; an equal indent with a
 * different kind starts a sibling list. Returns the top-level list node(s) (usually one).
 */
function buildListTree(items) {
    const roots = [];
    const stack = [];
    for (const it of items) {
        const type = it.ordered ? 'orderedList' : 'bulletList';
        while (stack.length > 0 && stack[stack.length - 1].indent > it.indent) {
            stack.pop();
        }
        const top = stack[stack.length - 1];
        if (top && top.indent === it.indent && top.list.type === type) {
            // Sibling within the same list.
            const li = makeListItem(it.text);
            top.list.content.push(li);
            top.lastItem = li;
            continue;
        }
        const li = makeListItem(it.text);
        const list = { type, content: [li] };
        if (top && top.indent === it.indent) {
            // Same indent, different marker kind → a new sibling list beside the current one.
            stack.pop();
            const parent = stack[stack.length - 1];
            if (parent)
                parent.lastItem.content.push(list);
            else
                roots.push(list);
        }
        else if (top && it.indent > top.indent) {
            // Deeper → nest under the current item.
            top.lastItem.content.push(list);
        }
        else {
            // No shallower list open → a new top-level list.
            roots.push(list);
        }
        stack.push({ indent: it.indent, list, lastItem: li });
    }
    return roots;
}
function parseMarkdownToTiptap(markdown) {
    const lines = markdown.replace(/\r\n?/g, '\n').split('\n');
    const content = [];
    let index = 0;
    while (index < lines.length) {
        const rawLine = lines[index] ?? '';
        const line = rawLine.trimEnd();
        if (!line.trim()) {
            index += 1;
            continue;
        }
        const codeFence = getCodeFenceMarker(line);
        if (codeFence) {
            const codeLines = [];
            index += 1;
            while (index < lines.length) {
                const nextLine = lines[index] ?? '';
                if (getCodeFenceMarker(nextLine) === codeFence) {
                    index += 1;
                    break;
                }
                codeLines.push(nextLine);
                index += 1;
            }
            content.push({
                type: 'codeBlock',
                content: codeLines.length > 0 ? [createTextNode(codeLines.join('\n'))] : undefined,
            });
            continue;
        }
        if (/^[-*_]{3,}\s*$/.test(line.trim())) {
            content.push({ type: 'horizontalRule' });
            index += 1;
            continue;
        }
        const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
        if (headingMatch) {
            content.push({
                type: 'heading',
                attrs: { level: headingMatch[1].length },
                content: parseInlineMarkdown(headingMatch[2] ?? ''),
            });
            index += 1;
            continue;
        }
        if (isMarkdownTableStart(lines, index)) {
            const headers = splitMarkdownTableRow(lines[index] ?? '');
            const rows = [];
            index += 2;
            while (index < lines.length) {
                const rowLine = lines[index] ?? '';
                if (!rowLine.trim() || !rowLine.includes('|'))
                    break;
                rows.push(splitMarkdownTableRow(rowLine));
                index += 1;
            }
            const headerRow = {
                type: 'tableRow',
                content: headers.map((cell) => ({
                    type: 'tableHeader',
                    content: [createParagraphFromText(cell)],
                })),
            };
            const bodyRows = rows.map((row) => ({
                type: 'tableRow',
                content: headers.map((_, cellIndex) => ({
                    type: 'tableCell',
                    content: [createParagraphFromText(row[cellIndex] ?? '')],
                })),
            }));
            content.push({ type: 'table', content: [headerRow, ...bodyRows] });
            continue;
        }
        if (/^>\s?/.test(line)) {
            const quoteLines = [];
            while (index < lines.length) {
                const quoteLine = lines[index] ?? '';
                if (!/^>\s?/.test(quoteLine.trim()))
                    break;
                quoteLines.push(quoteLine.trim().replace(/^>\s?/, ''));
                index += 1;
            }
            content.push({
                type: 'blockquote',
                content: quoteLines.map((quoteLine) => createParagraphFromText(quoteLine)),
            });
            continue;
        }
        if (LIST_ITEM_RE.test(line)) {
            const items = [];
            while (index < lines.length) {
                const listLine = (lines[index] ?? '').trimEnd();
                const match = listLine.match(LIST_ITEM_RE);
                if (!match)
                    break;
                const lead = match[1] ?? '';
                items.push({
                    indent: lead.replace(/\t/g, '  ').length,
                    ordered: /\d/.test(match[2] ?? ''),
                    text: (match[3] ?? '').trim(),
                });
                index += 1;
            }
            content.push(...buildListTree(items));
            continue;
        }
        const paragraphLines = [line];
        index += 1;
        while (index < lines.length) {
            const nextLine = (lines[index] ?? '').trimEnd();
            if (!nextLine.trim())
                break;
            if (getCodeFenceMarker(nextLine) ||
                /^[-*_]{3,}\s*$/.test(nextLine.trim()) ||
                /^(#{1,6})\s+/.test(nextLine) ||
                /^>\s?/.test(nextLine.trim()) ||
                /^[-*+]\s+/.test(nextLine.trim()) ||
                /^(\d+)\.\s+/.test(nextLine.trim()) ||
                isMarkdownTableStart(lines, index)) {
                break;
            }
            paragraphLines.push(nextLine);
            index += 1;
        }
        content.push(createParagraphFromText(paragraphLines.join('\n')));
    }
    return { type: 'doc', content };
}
function serializeTextNode(node) {
    const rawText = typeof node.text === 'string' ? node.text : '';
    const marks = Array.isArray(node.marks) ? node.marks : [];
    return marks.reduce((text, mark) => {
        switch (mark.type) {
            case 'bold':
                return `**${text}**`;
            case 'italic':
                return `*${text}*`;
            case 'strike':
                return `~~${text}~~`;
            case 'highlight':
                return `==${text}==`;
            case 'code':
                return `\`${text}\``;
            case 'link': {
                const href = isRecord(mark.attrs) && typeof mark.attrs.href === 'string' ? mark.attrs.href : '';
                return href ? `[${text}](${href})` : text;
            }
            default:
                return text;
        }
    }, rawText);
}
/** Order-insensitive identity of a node's marks, so equally-marked neighbours can merge. */
function marksKey(marks) {
    if (!Array.isArray(marks))
        return '';
    return marks
        .filter(isRecord)
        .map((m) => JSON.stringify({ type: m.type, attrs: m.attrs ?? null }))
        .sort()
        .join('|');
}
function serializeInlineNodes(nodes) {
    // Text and token nodes become marked pieces; consecutive pieces with the same marks are
    // merged before serializing, so `**Plan (**` + bold token + `**)**` comes out as one
    // `**Plan ({{protoss:...}})**` span. Serializing each node on its own would emit `****`,
    // which the inline parser cannot read back, and the token would lose its bold.
    const pieces = [];
    const pushMarked = (text, marks) => {
        const key = marksKey(marks);
        const prev = pieces[pieces.length - 1];
        if (prev && 'key' in prev && prev.key === key) {
            prev.text += text;
            return;
        }
        pieces.push({ text, marks: Array.isArray(marks) ? marks : [], key });
    };
    for (const node of nodes) {
        if (!isRecord(node))
            continue;
        if (node.type === 'text') {
            pushMarked(typeof node.text === 'string' ? node.text : '', node.marks);
        }
        else if (node.type === 'hardBreak') {
            pieces.push({ raw: '\n' });
        }
        else if (node.type === 'protossToken') {
            // Inline `{{protoss:...}}` token node (rendered read-only/resolved in the editor):
            // serialize back to its raw token text so it survives the editor->markdown round-trip
            // (without this it would be dropped, losing the token from sowContent). Its marks
            // (bold, italic, a user highlight covering it) travel with it.
            const token = isRecord(node.attrs) ? String(node.attrs.token ?? '') : '';
            if (token)
                pushMarked(`{{${token}}}`, node.marks);
        }
        else if (Array.isArray(node.content)) {
            pieces.push({ raw: serializeInlineNodes(node.content) });
        }
    }
    return pieces
        .map((p) => ('raw' in p ? p.raw : serializeTextNode({ text: p.text, marks: p.marks })))
        .join('');
}
function serializeBlockNode(node, depth = 0) {
    const childContent = Array.isArray(node.content) ? node.content : [];
    switch (node.type) {
        // Read-only data-table node (SOW editor): a view-only injection of a resolved
        // dynamicBlock. It is NEVER serialized into sowContent - the server recomputes
        // sowContent = tiptapToMarkdown(tiptapContent) on every autosave, so this empty
        // case is the authoritative guarantee that data tables can't leak into the prose.
        case 'sowDataTable':
            return '';
        // Editable AI-section node (SOW editor): its prose is persisted to
        // `generationFlags.aiSections` (harvested via extractAiSectionsFromTiptap), NOT into
        // sowContent - so it never leaks into the prose, keeping the AI/fixed provenance split.
        case 'sowAiSection':
            return '';
        case 'paragraph':
            return serializeInlineNodes(childContent);
        case 'heading': {
            const level = isRecord(node.attrs) && typeof node.attrs.level === 'number' ? node.attrs.level : 1;
            return `${'#'.repeat(Math.max(1, Math.min(level, 6)))} ${serializeInlineNodes(childContent)}`.trim();
        }
        case 'bulletList':
            return childContent
                .map((child) => (isRecord(child) ? serializeListItem(child, '-', depth) : ''))
                .filter(Boolean)
                .join('\n');
        case 'orderedList':
            return childContent
                .map((child, index) => isRecord(child) ? serializeListItem(child, `${index + 1}.`, depth) : '')
                .filter(Boolean)
                .join('\n');
        case 'blockquote':
            return childContent
                .map((child) => {
                if (!isRecord(child))
                    return '';
                return serializeBlockNode(child)
                    .split('\n')
                    .map((line) => `> ${line}`.trimEnd())
                    .join('\n');
            })
                .filter(Boolean)
                .join('\n');
        case 'codeBlock':
            return ['```', serializeInlineNodes(childContent), '```'].join('\n');
        case 'horizontalRule':
            return '---';
        case 'table':
            return serializeTable(node);
        default:
            if (childContent.length > 0) {
                return childContent
                    .map((child) => (isRecord(child) ? serializeBlockNode(child, depth) : ''))
                    .filter(Boolean)
                    .join('\n');
            }
            return '';
    }
}
function serializeListItem(node, marker, depth) {
    const childContent = Array.isArray(node.content) ? node.content : [];
    const indent = '  '.repeat(depth);
    const out = [];
    let markerPlaced = false;
    for (const child of childContent) {
        if (!isRecord(child))
            continue;
        if (child.type === 'bulletList' || child.type === 'orderedList') {
            // Nested list: serialize one level deeper - its own lines already carry the right indent.
            const nested = serializeBlockNode(child, depth + 1);
            if (nested)
                out.push(nested);
            continue;
        }
        // Paragraph / other block → the item's text (and any continuation lines).
        const text = serializeBlockNode(child, depth);
        if (!text)
            continue;
        for (const textLine of text.split('\n')) {
            if (!markerPlaced) {
                out.push(`${indent}${marker} ${textLine}`);
                markerPlaced = true;
            }
            else {
                out.push(`${indent}  ${textLine}`);
            }
        }
    }
    if (!markerPlaced)
        out.unshift(`${indent}${marker}`);
    return out.join('\n');
}
function serializeTable(node) {
    const rows = Array.isArray(node.content) ? node.content : [];
    if (rows.length === 0)
        return '';
    const parsedRows = rows
        .map((row) => {
        if (!isRecord(row) || !Array.isArray(row.content))
            return [];
        return row.content.map((cell) => {
            if (!isRecord(cell) || !Array.isArray(cell.content))
                return '';
            return (cell.content
                .map((child) => (isRecord(child) ? serializeBlockNode(child) : ''))
                .filter(Boolean)
                .join('\n')
                .trim()
                // Escape literal pipes so cell text cannot be mis-read as a column
                // boundary on re-parse (splitMarkdownTableRow honors the \| escape).
                .replace(/\|/g, '\\|')
                // Re-emit every intra-cell line break as <br> - the exact inverse of
                // createParagraphFromText (which splits cells on /\r?\n|<br\/?>/). This
                // keeps multi-line + bulleted cells intact across a save round-trip
                // instead of collapsing them to spaces.
                .replace(/\n/g, '<br>'));
        });
    })
        .filter((row) => row.length > 0);
    if (parsedRows.length === 0)
        return '';
    const header = parsedRows[0] ?? [];
    const separator = header.map(() => '---');
    const body = parsedRows.slice(1);
    return [header, separator, ...body].map((row) => `| ${row.join(' | ')} |`).join('\n');
}
/**
 * Parse raw content (JSON Tiptap doc, Markdown, or plain text) into a Tiptap doc object.
 */
export function parseContentToTiptap(raw) {
    try {
        const doc = JSON.parse(raw);
        if (isTiptapDoc(doc))
            return doc;
    }
    catch {
        // not JSON - fall through
    }
    const normalized = raw.replace(/\r\n?/g, '\n').trim();
    if (!normalized) {
        return { type: 'doc', content: [] };
    }
    return parseMarkdownToTiptap(normalized);
}
/** Extract plain text from a Tiptap doc JSON (for isValid / dirty comparison). */
export function tiptapToPlainText(doc) {
    function collect(nodes) {
        return nodes
            .map((node) => {
            if (!isRecord(node))
                return '';
            if (node.type === 'text')
                return typeof node.text === 'string' ? node.text : '';
            if (node.type === 'hardBreak')
                return '\n';
            const inner = Array.isArray(node.content) ? collect(node.content) : '';
            switch (node.type) {
                case 'paragraph':
                case 'heading':
                case 'blockquote':
                case 'codeBlock':
                case 'tableCell':
                case 'tableHeader':
                    return `${inner}\n`;
                case 'listItem':
                    return `${inner}\n`;
                case 'bulletList':
                case 'orderedList':
                case 'tableRow':
                case 'table':
                case 'doc':
                    return inner;
                default:
                    return inner;
            }
        })
            .join('');
    }
    return collect(doc.content ?? [])
        .replace(/\n{3,}/g, '\n\n')
        .trimEnd();
}
/** Serialize a Tiptap document back into Markdown for persistence. */
export function tiptapToMarkdown(doc) {
    const blocks = Array.isArray(doc.content) ? doc.content : [];
    return blocks
        .map((block) => (isRecord(block) ? serializeBlockNode(block) : ''))
        .filter((block) => block.trim().length > 0)
        .join('\n\n')
        .trim();
}
/**
 * Remove read-only `sowDataTable` injection nodes from a Tiptap doc (immutable).
 * Used to keep the SOW editor's stored tiptapContent prose-only and to make
 * data-table injection idempotent (strip-then-reinject).
 */
export function stripSowDataTableNodes(doc) {
    return stripNodesByType(doc, 'sowDataTable');
}
/**
 * Remove editable `sowAiSection` nodes from a Tiptap doc (immutable). Used to make
 * AI-section injection idempotent (strip-then-reinject), mirroring the data-table strip.
 */
export function stripSowAiSectionNodes(doc) {
    return stripNodesByType(doc, 'sowAiSection');
}
function stripNodesByType(doc, type) {
    if (!isRecord(doc))
        return doc;
    const strip = (node) => {
        if (!isRecord(node))
            return node;
        if (node.type === type)
            return null;
        if (Array.isArray(node.content)) {
            return { ...node, content: node.content.map(strip).filter((n) => n !== null) };
        }
        return node;
    };
    return strip(doc);
}
/**
 * Harvest each editable `sowAiSection` node's prose back to markdown, keyed by its `slug`.
 * The SOW editor edits AI sections inline (as `sowAiSection` nodes); on save this extracts
 * their content into `generationFlags.aiSections` (where the `source:'ai'` band reads it
 * from), keeping AI out of `sowContent` while staying editable + banded.
 */
export function extractAiSectionsFromTiptap(doc) {
    const out = {};
    const walk = (node) => {
        if (!isRecord(node))
            return;
        if (node.type === 'sowAiSection') {
            const slug = isRecord(node.attrs) ? String(node.attrs.slug ?? '') : '';
            if (slug) {
                out[slug] = tiptapToMarkdown({
                    type: 'doc',
                    content: Array.isArray(node.content) ? node.content : [],
                });
            }
            return; // AI sections don't nest
        }
        if (Array.isArray(node.content))
            node.content.forEach(walk);
    };
    if (isRecord(doc) && Array.isArray(doc.content))
        doc.content.forEach(walk);
    return out;
}
/**
 * What a SOW save actually persists, as one comparable string: the prose markdown
 * (`sowContent`) plus the AI-section prose (`generationFlags.aiSections`). Two docs with the
 * same key save the same SOW, so a save between them is not a user edit. Editor-only noise
 * drops out: default attrs, the read-only data tables, and the empty paragraph Tiptap's
 * trailing-node rule appends after a table at the end of the doc (that one fired an update on
 * open, autosaved, and marked an untouched SOW as user-edited).
 */
export function sowContentKey(markdown, aiSections) {
    const ai = Object.entries(aiSections ?? {})
        .filter(([, text]) => text.trim().length > 0)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return JSON.stringify([markdown.trim(), ai]);
}
/** `sowContentKey` of a SOW editor doc (the prose and AI sections a save would harvest). */
export function sowEditorContentKey(doc) {
    if (!isRecord(doc))
        return sowContentKey('', null);
    return sowContentKey(tiptapToMarkdown(doc), extractAiSectionsFromTiptap(doc));
}
//# sourceMappingURL=tiptap.js.map